<script lang="ts">
    import { App } from "@/constants";
    import { ModalToken } from "@/modal-manager";
    import { modalManagerContextKey } from "@/modal-manager/constants";
    import InstagramIcon from "@apps/instagram/components/icons/InstagramIcon.svelte";
    import { INSTAGRAM_TRACKING_APP_NAME } from "@apps/instagram/constants";
    import InstagramTaggedProductQuery from "@apps/instagram/queries/InstagramTaggedProductQuery";
    import { createInstagramPostReferrer } from "@apps/instagram/utils/createInstagramPostReferrer";
    import { instagramGalleryWidgetContextToken } from "@apps/instagram/widgets/instagram_gallery_widget/context_tokens";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
    import type ProductVariantsEntity from "@modules/products/entities/ProductVariantsEntity";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import SelectProductVariantManager from "@modules/products/utils/SelectProductVariantManager";
    import CN from "classnames";
    import {
        combineLatest,
        distinctUntilChanged,
        filter,
        lastValueFrom,
        map,
        mergeMap,
        of,
        startWith,
        switchMap,
    } from "rxjs";
    import { onMount } from "svelte";

    import Divider from "@components/Divider.svelte";
    import SelectOptionField from "@components/forms/SelectOptionField/SelectOptionField.svelte";
    import Icon from "@components/Icon.svelte";
    import ManagedModalWithDialog from "@components/Modal/ManagedModalWithDialog.svelte";
    import PostModalProductImage from "@components/PostModal/PostModalProductImage.svelte";
    import PostModalProductInfo from "@components/PostModal/PostModalProductInfo.svelte";
    import PostModalProductView from "@components/PostModal/PostModalProductView.svelte";
    import T from "@components/T.svelte";
    import Tabs from "@components/Tabs/Tabs.svelte";
    import TabsManager from "@components/Tabs/TabsManager";
    import TabsMenu from "@components/Tabs/TabsMenu.svelte";
    import TabsPanel from "@components/Tabs/TabsPanel.svelte";
    import type { TabItem } from "@components/Tabs/types";

    import InstagramQuestionsTab from "./components/InstagramQuestionsTab.svelte";
    import InstagramReviewsTab from "./components/InstagramReviewsTab.svelte";
    import InstagramPostModalManager from "./InstagramPostModalManager";
    import InstagramPostModalPostView from "./InstagramPostModalPostView.svelte";

    export let postModalToken: ModalToken<{ id: number }>;
    export let instagramPostModalManager: InstagramPostModalManager;
    export let highQualityEnabled: boolean;
    export let showReviewAndQuestions: boolean;
    export let productQuickViewEnabled: boolean;
    export let hideBranding: boolean;
    export let popupTheme: "light" | "dark";
    export let taggedProductQuery: InstagramTaggedProductQuery;
    export let productsQuery: ProductsQuery;

    const tabsManager = new TabsManager();

    const openedProductTag$ = instagramPostModalManager.openedProductTag$; // opened product tag
    const openedTaggedProduct$ = taggedProductQuery.product$; // additional information about opened product
    const product$ = openedProductTag$.pipe(
        switchMap((openedProductTag) => {
            return openedProductTag?.product.id
                ? productsQuery.selectProduct(openedProductTag?.product.id)
                : of(null);
        })
    );
    const activeMediaProductTags$ =
        instagramPostModalManager.activeMediaProductTags$;
    const postId$ = instagramPostModalManager.activePost$.pipe(
        map((post) => post?.id)
    );
    const activePostLink$ = instagramPostModalManager.activePost$.pipe(
        map((post) => post?.permalink)
    );
    const adminSelectedVariant$ = openedProductTag$.pipe(
        map((openedProductTag) =>
            openedProductTag?.variantId
                ? openedProductTag.product.variant
                : null
        )
    );
    const defaultVariantId$ = combineLatest([
        product$,
        adminSelectedVariant$,
    ]).pipe(
        map(([product, adminSelectedVariant]) =>
            adminSelectedVariant
                ? adminSelectedVariant.id
                : product?.variants[0]?.id ?? null
        )
    );
    const selectProductVariantManager = new SelectProductVariantManager(
        product$,
        defaultVariantId$
    );
    const selectedVariant$ =
        selectProductVariantManager.selectedProductVariant$;
    const selectedVariantImageId$ = selectedVariant$.pipe(
        map((selectedVariant) => {
            return selectedVariant && selectedVariant.image
                ? selectedVariant.image.id
                : null;
        })
    );

    const context = getTypedContext(instagramGalleryWidgetContextToken);

    const {
        instagramTrackersService,
        trackingApiService,
        cartService,
        cartQuery,
        locationController,
        gwi18n,
        urlsBuilder,
        htmlSanitizer,
        cartAvailabilityProvider,
        modulesStatusesProvider,
    } = context;
    const isCartAvailable = cartAvailabilityProvider.isCartAvailable();
    const modalManager = getTypedContext(modalManagerContextKey);

    const sanitizedDescription$ = openedTaggedProduct$.pipe(
        map((product) => {
            const description = product?.product.description;

            return description
                ? htmlSanitizer.sanitize(description, {
                      span: ["class", "id", "style"],
                      div: ["class", "id", "style"],
                      p: ["class", "id", "style"],
                      i: ["class", "id"],
                      b: ["class", "id"],
                      pre: ["class", "id"],
                      small: ["class", "id"],
                      strong: ["class", "id"],
                      a: ["class", "href", "target"],
                      em: ["class", "id"],
                      ul: ["class", "id"],
                      ol: ["class", "id"],
                      li: ["class", "id"],
                      meta: ["charset"],
                      br: [],
                      h1: ["class", "id", "style"],
                      h2: ["class", "id", "style"],
                      h3: ["class", "id", "style"],
                      h4: ["class", "id", "style"],
                      h5: ["class", "id", "style"],
                      h6: ["class", "id", "style"],
                  })
                : "";
        })
    );

    modalManager.addModal(postModalToken);
    const modalParams$ = modalManager.selectParams(postModalToken);
    const modalIsOpen$ = modalManager.selectIsOpen(postModalToken);
    const descriptionT$ = gwi18n.selectT(
        "instagram.critical.InstagramGalleryWidget",
        "description"
    );
    const tabs$ = combineLatest([openedTaggedProduct$, descriptionT$]).pipe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        filter(([openedProduct, _]) => !!openedProduct),
        map(([openedProduct, descriptionT]) => {
            const tabs: TabItem[] = [
                {
                    name: "description",
                    label: descriptionT,
                },
            ];
            // Reviews&Questions будут показываться только в Growave аппе
            if (showReviewAndQuestions) {
                tabs.push(
                    {
                        name: "reviews",
                        label: "Reviews",
                        tag: openedProduct?.reviewsCount,
                    },
                    {
                        name: "questions",
                        label: "Questions",
                        tag: openedProduct?.questionsCount,
                    }
                );
            }
            return tabs;
        }),
        startWith([])
    );

    const cartStatus$ = selectedVariant$.pipe(
        filter((v): v is ProductVariantsEntity => !!v),
        switchMap((selectedVariant) => {
            return cartQuery.selectEditItemStatus(selectedVariant.id);
        })
    );
    const handleClose = () => {
        modalManager.closeModal(postModalToken);
    };
    const handleChange = (value: string, index: number) =>
        selectProductVariantManager.changeOption({ value, index });

    const handleCheckout = () =>
        locationController.goTo(urlsBuilder.createCartUrl());

    const handleAddToCart = async () => {
        if (!$selectedVariant$ || !$postId$) {
            return;
        }

        const cart = await cartService.addToCart([
            {
                variantID: $selectedVariant$.id,
                quantity: 1,
            },
        ]);
        if (!$modalParams$) {
            return;
        }

        await lastValueFrom(
            trackingApiService.trackAddToCart({
                cartToken: cart.token,
                variantId: $selectedVariant$.id,
                appName: INSTAGRAM_TRACKING_APP_NAME,
                cartReferrer: createInstagramPostReferrer($postId$),
            })
        );
    };
    onMount(() => {
        cartService.getCart();
        const modalParamsSubscription = modalParams$.subscribe((params) => {
            if (!params) return;
            instagramPostModalManager.changeActivePost(params.id);
        });
        const activePostSubscription = instagramPostModalManager.activePost$
            .pipe(distinctUntilChanged())
            .subscribe((activePost) => {
                if (activePost) {
                    lastValueFrom(
                        instagramTrackersService.trackPostView(activePost.id)
                    );
                }
            });
        const modalOpenSubscription = modalIsOpen$.subscribe((isOpen) => {
            if (!isOpen) instagramPostModalManager.openProductTag(null);
        });
        const tabsSubscription = tabs$
            .pipe(mergeMap((v) => v))
            .subscribe((t) => {
                tabsManager.registerTab(t.name);
            });
        return () => {
            modalParamsSubscription.unsubscribe();
            modalOpenSubscription.unsubscribe();
            tabsSubscription.unsubscribe();
            activePostSubscription.unsubscribe();
        };
    });
    $: rerenderKey = $product$?.id;

    const reviewsAppStatus$ = modulesStatusesProvider.selectModuleStatus(
        App.Reviews
    );
    const questionsAppStatus$ = modulesStatusesProvider.selectModuleStatus(
        App.Questions
    );
</script>

<ManagedModalWithDialog
    dialogClass={CN("gw-ig-post-modal__dialog", `gw-post-modal__${popupTheme}`)}
    modalClass="gw-ig-post-modal"
    modalStyle="--gw-modal-padding: 0"
    variant="fullscreen"
    modalToken={postModalToken}
>
    {#if $openedProductTag$}
        <PostModalProductView
            productViewController={{
                nextProductTag: () =>
                    instagramPostModalManager.nextProductTag(),
                prevProductTag: () =>
                    instagramPostModalManager.prevProductTag(),
                close: () => instagramPostModalManager.openProductTag(null),
            }}
            showControlls={$activeMediaProductTags$.length > 1}
            on:close={handleClose}
        >
            <a
                href={$activePostLink$}
                class="gw-ig-post-modal-post-view__ig-link gw-link-reset-default-styles"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Opens post on instagram in a new tab"
                slot="activePostLink"
            >
                <Icon
                    width={18}
                    height={18}
                    class="gw-ig-post-modal-post-view__ig-link-icon"
                >
                    <InstagramIcon />
                </Icon>
                <span class="gw-ig-post-modal-post-view__ig-link-text">
                    <T
                        namespace="instagram.critical.InstagramGalleryWidget"
                        translationKey="view_on_instagram"
                    />
                </span>
            </a>
            <svelte:fragment slot="productImage">
                {#key rerenderKey}
                    <PostModalProductImage
                        images={$product$ ? $product$.images : []}
                        imageId$={selectedVariantImageId$}
                        altText={$product$?.title}
                    />
                {/key}
            </svelte:fragment>
            {#if $product$}
                <PostModalProductInfo
                    slot="productInfo"
                    selectedVariant={$selectedVariant$
                        ? {
                              title: $selectedVariant$.title,
                              id: $selectedVariant$.id,
                              compareAtPrice:
                                  $selectedVariant$.compareAtPrice?.amount ||
                                  null,
                              price: $selectedVariant$.price.amount,
                              available: $selectedVariant$.availableForSale,
                          }
                        : null}
                    productTitle={$product$.title || ""}
                    productUrl={urlsBuilder.createProductUrl(
                        $product$.handle,
                        $selectedVariant$?.id,
                        $postId$
                            ? createInstagramPostReferrer($postId$)
                            : undefined
                    )}
                    on:addToCard={handleAddToCart}
                    on:handleCheckout={handleCheckout}
                    cartStatus={$cartStatus$}
                    {isCartAvailable}
                >
                    <svelte:fragment>
                        {#if $adminSelectedVariant$}
                            <div
                                class="gw-post-modal-product-info__variant-title"
                            >
                                {$adminSelectedVariant$.title}
                            </div>
                        {:else}
                            <div class="gw-post-modal-product-info__variants">
                                {#each $product$.options.filter((option) => option.values.length > 1 || ($product$?.options?.length ?? 0) > 1) as option, idx (option.name)}
                                    <SelectOptionField
                                        optionsSet={{
                                            name: option.name,
                                            options: option.values.map(
                                                (value) => ({
                                                    label: value,
                                                    value,
                                                })
                                            ),
                                        }}
                                        name={`productOption-${option.name}`}
                                        value={selectProductVariantManager.getByIndex(
                                            idx
                                        )}
                                        on:change={(e) =>
                                            handleChange(e.detail, idx)}
                                    />
                                {/each}
                            </div>
                        {/if}
                    </svelte:fragment>
                </PostModalProductInfo>
            {/if}
            <Divider />
            <Tabs
                class="gw-post-modal-product-view__tabs"
                tabsName="ig-post-modal-product-view-tab"
            >
                <svelte:fragment slot="menu">
                    {#if $tabs$}
                        <TabsMenu
                            {tabsManager}
                            tabs={$tabs$}
                            position="start"
                        />
                    {/if}
                </svelte:fragment>
                <svelte:fragment slot="body">
                    <TabsPanel {tabsManager} name={"description"}>
                        <div
                            class="gw-post-modal-product-view__tabpanel-wrapper"
                        >
                            {@html $sanitizedDescription$}
                        </div>
                    </TabsPanel>
                    {#if showReviewAndQuestions && $reviewsAppStatus$ === MODULE_STATUS.INITIALIZED}
                        <TabsPanel {tabsManager} name={"reviews"}>
                            <div
                                class="gw-post-modal-product-view__tabpanel-wrapper"
                            >
                                {#if $product$}
                                    <InstagramReviewsTab
                                        productId={$product$.id}
                                    />
                                {/if}
                            </div>
                        </TabsPanel>
                    {/if}
                    {#if showReviewAndQuestions && $questionsAppStatus$ === MODULE_STATUS.INITIALIZED}
                        <TabsPanel {tabsManager} name={"questions"}>
                            <div
                                class="gw-post-modal-product-view__tabpanel-wrapper"
                            >
                                {#if $product$}
                                    <InstagramQuestionsTab
                                        productId={$product$.id}
                                    />
                                {/if}
                            </div>
                        </TabsPanel>
                    {/if}
                </svelte:fragment>
            </Tabs>
        </PostModalProductView>
    {:else}
        <InstagramPostModalPostView
            {instagramPostModalManager}
            {highQualityEnabled}
            {productQuickViewEnabled}
            {hideBranding}
            on:close={handleClose}
        />
    {/if}
</ManagedModalWithDialog>

<style>
    /* TODO как можно сделать без обращения к gw-modal */
    :global(.gw-modal.gw-ig-post-modal .gw-ig-post-modal__dialog) {
        width: 100vw;
        border-radius: 0;
        margin: 0;
    }

    :global(.gw-ig-post-modal .gw-modal-header__close_btn) {
        visibility: hidden;
    }

    .gw-post-modal-product-view__tabpanel-wrapper {
        padding: 20px 0 10px;
    }

    :global(.gw-post-modal-product-view__tabpanel-wrapper *) {
        color: var(--gw-post-modal-text-color);
    }

    .gw-post-modal-product-info__variant-title {
        margin: 0;
        margin-bottom: 32px;
        font-size: 1em;
        color: var(--gw-post-modal-text-color);
    }

    .gw-post-modal-product-info__variants:not(:empty) {
        display: flex;
        flex-wrap: wrap;
        padding: 16px 0 32px;
        row-gap: 16px;
    }

    .gw-post-modal-product-info__variants:empty + :global(.gw-separator) {
        display: none;
    }

    .gw-ig-post-modal-post-view__ig-link {
        display: flex;
        align-items: center;
        width: fit-content;
        padding: 8px;
        -moz-column-gap: 10px;
        column-gap: 10px;
        border-radius: var(--gw-border-radius-base);
        background: var(--gw-lightgrey-bg);
        color: #fff;
        font-size: 0.8em;
        line-height: 0;
    }

    :global(.gw-ig-posts-modal-posts-view__ig-link-icon) {
        width: 22px;
        height: 22px;
        padding: 2px;
        text-align: center;
        vertical-align: middle;
        background: radial-gradient(
            125.51% 502.1% at 0.14% 201.03%,
            #fec053 0%,
            #f2203e 32.73%,
            #b729a8 64.85%,
            #5342d6 100%
        );
        border-radius: 5px;
    }
    .gw-ig-post-modal-post-view__ig-link-text {
        font-size: var(--gw-font-size-xlg);
        color: var(--gw-post-modal-text-color);
    }
    :global(.gw-post-modal-product-info__variants .gw-select-option label) {
        margin-bottom: 15px;
    }

    :global(.gw-post-modal-product-view__tabs) {
        --gw-tabs-menu-font-size: var(--gw-font-size-lg);
    }
</style>
